/**
 * Shopify writes. Each fn unwraps `{ data: T }` and throws ApiError on failure.
 */
import type {
  ShopifyConnectIntent,
  ShopifyUninstallRequest,
  ShopifyUninstallResult,
} from '@discovr/types';
import { INTEGRATION_BASE, postData } from './shared';

/** Claim a Shopify-initiated install: binds the pending_claim token to the
 *  logged-in brand. Called after the merchant is redirected back with
 *  `?shopify=installed#claim=<token>` and has confirmed the store. */
const claimInstall = (token: string): Promise<{ connected: boolean; shop_domain: string }> =>
  postData<{ connected: boolean; shop_domain: string }>(`${INTEGRATION_BASE}/claim`, {
    claim_token: token,
  });

// The BE re-verifies confirm === "UNINSTALL" (CONFIRM_MISMATCH 400), so the
// typed gate can't be bypassed by calling the API directly.
/** The BE can spend up to ~90s turning codes off and calling Shopify; the shared
 *  client's 30s default would abort a healthy uninstall. */
const UNINSTALL_TIMEOUT_MS = 95_000;

const uninstall = (opts: {
  turnOffCodes: boolean;
  continueAnyway: boolean;
}): Promise<ShopifyUninstallResult> =>
  postData<ShopifyUninstallResult>(
    `${INTEGRATION_BASE}/uninstall`,
    {
      confirm: 'UNINSTALL',
      turn_off_codes: opts.turnOffCodes,
      continue: opts.continueAnyway,
    } satisfies ShopifyUninstallRequest,
    { timeout: UNINSTALL_TIMEOUT_MS },
  );

/** Mint the single-use nonce for a brand-bound install. The caller then submits
 *  it as a top-level form POST to `submit_url`. A plain XHR can't carry the
 *  cookie that follows. */
const createConnectIntent = (): Promise<ShopifyConnectIntent> =>
  postData<ShopifyConnectIntent>(`${INTEGRATION_BASE}/connect-intent`);

export function createShopifyMutationsApi() {
  return { claimInstall, uninstall, createConnectIntent };
}

export type ShopifyMutationsApi = ReturnType<typeof createShopifyMutationsApi>;
